// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { removeTab } from 'wdna-components/lib/tabs';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import Loader from 'wdna-components/lib/loaders/local/Loader';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { messageError, messageInfo } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { variable, VariableDO } from '../../requests';

import ButtonsForm from '../../../../components/ButtonsForm';
import Form from './Form';

interface Props {
  rowId?: number;
  notify?: () => void;
}

interface State {
  loading: boolean;
  data: Partial<VariableDO>;
}

export default class Edit extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private defaultValues: any;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.defaultValues = {};

    this.state = {
      loading: false,
      data: {
        alarm: false,
        graph: false,
      }
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load();
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = (): void => {
    const { rowId } = this.props;
    if (rowId && !isNaN(rowId)) {
      this.loadingStart();
      variable<VariableDO>('get', String(rowId))
        ?.then((resp: VariableDO) => {
          this.setLocalState({
            data: {
              ...resp,
            }
          }, () => {
            this.defaultValues = _.cloneDeep(resp);
          });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    }
  };

  public loadingStart = (): void => {
    this.setLocalState({ loading: true });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setLocalState({ loading: false });
    window.store.dispatch(finishProcess(this.pid));
  };

  public onReset = (): void => {
    this.setLocalState({ data: { ...this.defaultValues } });
  };

  public submit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const { data } = this.state;

    this.loadingStart();
    variable<VariableDO>('set', undefined, data)
      ?.then(() => {
        messageInfo('Success', `Variable successfully ${data.id ? 'edited' : 'created'}`, 5000);
        const { notify } = this.props;
        if (typeof notify === 'function') {
          notify();
        }
        window.store.dispatch(removeTab('VE'));
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
    const { ctype } = dataset || {};
    // Process new value from ctype
    let newValue: any = (type === 'checkbox') ? !!checked : value;
    if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
    // Create new data state structure
    const levels: string[] = name.split('.');
    const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
    _.set(nextState, levels, newValue);
    this.setLocalState(nextState);
  };

  public render(): JSX.Element {
    const { loading, data } = this.state;

    return (
      <div id="content" className="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
                fullscreenbutton={false}
              >
                <div className="widget-body">
                  <Loader show={loading} overlay={false} />
                  <UiValidate>
                    <form className="smart-form" onSubmit={this.submit} method="post" encType="multipart/form-data">
                      <header>
                        <h2>
                          <span className="widget-icon" style={{ padding: '8px' }}>
                            <i className="fa fa-edit" />
                          </span>
                          Variable {data.id ? `Edit: ${data.id}` : 'New'}
                        </h2>
                      </header>
                      <Form formValues={this.state} onChange={this.handleChange} />
                      <ButtonsForm loading={loading} disabled={!hasLevel('role', ['ADM'])} onReset={this.onReset} />
                    </form>
                  </UiValidate>
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
